import React, { useState, useCallback } from 'react';
import Attachment from 'components/ui/Attachment';
import { atualizarIniciativa } from 'services/iniciativa-premio.service';
import { uploadFile } from 'services/file.service';
import { CATEGORIAS_INICIATIVAS_OFICIAIS, getCategoriaLabel, normalizeCategoryPremio } from 'utils/iniciativa-categoria';

interface ModalEditarInscricaoProps {
  iniciativaOriginal: any;
  onSave: () => void;
  onClose: () => void;
}

export const ModalEditarInscricao: React.FC<ModalEditarInscricaoProps> = ({
  iniciativaOriginal,
  onSave,
  onClose,
}) => {
  const currentDocumento = iniciativaOriginal?.documento || [];
  const currentCategory = normalizeCategoryPremio(iniciativaOriginal?.categoryPremio) || '';
  const isEcbb = currentCategory.startsWith('ECBB');

  const [videoUrl, setVideoUrl] = useState(() => {
    const video = currentDocumento.find((d: any) => d?.type === 'video');
    return video?.file || '';
  });
  const [videoEmbedUrl, setVideoEmbedUrl] = useState(() => {
    const video = currentDocumento.find((d: any) => d?.type === 'video');
    return video?.embedUrl || video?.file || '';
  });
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [categoryPremio, setCategoryPremio] = useState(currentCategory);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setError('');
    try {
      const documento: any[] = [];
      if (videoUrl) {
        documento.push({ file: videoUrl, embedUrl: videoEmbedUrl || videoUrl, type: 'video' });
      }

      const existingPdf = currentDocumento.find((d: any) => d?.type === 'file' && d?.file);
      if (pdfFile) {
        const fileData = await uploadFile(pdfFile);
        documento.push({ file: fileData.url, nameFile: fileData.filename, type: 'file' });
      } else if (existingPdf) {
        documento.push(existingPdf);
      }

      await atualizarIniciativa({
        ...iniciativaOriginal,
        documento,
        categoryPremio,
      });

      onSave();
    } catch (err) {
      console.error('Erro ao atualizar inscrição:', err);
      setError('Erro ao salvar alterações. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  }, [videoUrl, pdfFile, categoryPremio, iniciativaOriginal, currentDocumento, onSave]);

  return (
    <div className="bg-white rounded-2xl p-5 w-[680px] max-w-[90vw]">
      <h2 className="text-lg font-bold text-[#1e3a5f] mb-4">Editar Inscrição</h2>

      {/* Categoria — só editável para ECBB */}
      {isEcbb && (
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Categoria
          </label>
          <select
            value={categoryPremio}
            onChange={(e) => setCategoryPremio(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-bb"
          >
            <option value="" disabled>Selecione uma categoria</option>
            {CATEGORIAS_INICIATIVAS_OFICIAIS.filter((cat) => cat.id.startsWith('ECBB')).map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Video e Documento */}
      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-1">
          Vídeo e Documento
        </label>
        <Attachment
          currentDocument={currentDocumento}
          onFileSelect={(file) => setPdfFile(file)}
          onVideoSelect={setVideoUrl}
          onEmbedVideoSelect={setVideoEmbedUrl}
          onProgressComplete={() => {}}
          allowedTypes={['application/pdf']}
          maxFizeSize={3}
        />
      </div>

      {error && (
        <p className="text-red-500 text-sm mb-3">{error}</p>
      )}

      {/* Botões */}
      <div className="flex items-center justify-end gap-3">
        <button
          onClick={onClose}
          disabled={isSaving}
          className="bg-transparent border border-gray-300 text-gray-600 rounded-lg px-6 py-2.5 text-sm font-bold cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40"
        >
          CANCELAR
        </button>
        <button
          onClick={handleSave}
          disabled={isSaving || !videoUrl}
          className="bg-[#f5c518] text-[#1e3a5f] border-0 rounded-lg px-6 py-2.5 text-sm font-extrabold cursor-pointer hover:bg-[#e6b800] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isSaving ? 'SALVANDO...' : 'SALVAR ALTERAÇÕES'}
        </button>
      </div>
    </div>
  );
};
